import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../authprovider';

interface SignInOptions {
  successPath: string;
  onSuccess?: () => void;
}

interface SignInState {
  email: string;
  password: string;
  error: string | null;
  isLoading: boolean;
  showSessionAlert: boolean;
  sessionErrorMessage: string | null;
}

interface SignInActions {
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  dismissSessionAlert: () => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
}

/**
 * Custom hook for sign-in functionality
 */
export function useSignIn({ successPath, onSuccess }: SignInOptions): [SignInState, SignInActions] {
  // State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSessionAlert, setShowSessionAlert] = useState(false);
  const [sessionErrorMessage, setSessionErrorMessage] = useState<string | null>(null);
  
  // Hooks
  const { login, sessionExpired } = useAuth();
  const navigate = useNavigate();
  
  // Check for session expiration
  useEffect(() => {
    if (sessionExpired) {
      setShowSessionAlert(true);
      setSessionErrorMessage('Your session has expired. Please log in again to continue.');
    }
  }, [sessionExpired]);
  
  // Actions
  const clearError = () => setError(null);
  const dismissSessionAlert = () => setShowSessionAlert(false);
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    clearError();
    
    try {
      await login(email, password);
      if (onSuccess) {
        onSuccess();
      }
      navigate(successPath);
    } catch (error: any) {
      setIsLoading(false);
      
      // Check for actionable errors first
      if (error.response && error.response.data && error.response.data.errorCode === 'emailNeedsValidation') {
        localStorage.setItem('pendingVerificationEmail', error.response.data.params?.email || email);
        localStorage.setItem('actionableErrorMessage', error.response.data.message);
        navigate('/verification-needed');
        return;
      }
      
      // Default error handling for non-actionable errors
      try {
        console.log('Error object:', error);
        
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          console.log('Full error response data:', errorData);
          
          // Try to get detailed error message from response data
          if (typeof errorData.message === 'string') {
            // Use the specific error message from the backend
            setError(errorData.message);
          } else if (errorData.error && typeof errorData.error === 'string') {
            // Some APIs return the error in an 'error' field
            setError(errorData.error);
          } else if (errorData.reason && typeof errorData.reason === 'string') {
            // Some APIs use 'reason' field
            setError(errorData.reason);
          } else if (errorData.detail && typeof errorData.detail === 'string') {
            // Some APIs use 'detail' field
            setError(errorData.detail);
          } else if (typeof errorData === 'string') {
            // Sometimes the entire data is a string message
            setError(errorData);
          } else {
            // If we can't find a specific message, use a more descriptive fallback
            setError('Authentication failed. Please check your credentials and try again.');
          }
        } else if (error.message && typeof error.message === 'string') {
          // Use error object's message if available
          setError(error.message);
        } else {
          // Fallback for when we can't extract a specific message
          setError('Unable to sign in. Please check your email and password.');
        }
      } catch (e) {
        // Ultimate fallback if error handling itself fails
        console.error('Error while handling error:', e);
        setError('An unexpected error occurred. Please try again later.');
      }
    }
  };
  
  const state: SignInState = {
    email,
    password,
    error,
    isLoading,
    showSessionAlert,
    sessionErrorMessage
  };
  
  const actions: SignInActions = {
    setEmail,
    setPassword,
    setError,
    clearError,
    dismissSessionAlert,
    handleSubmit
  };
  
  return [state, actions];
}
